import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Loader2, LogOut, ShieldAlert, Inbox, Mail, Phone, RefreshCw, LogIn, Download, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import api, { setToken, clearToken } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [status, setStatus] = useState(location.state?.user ? "ready" : "checking"); // checking | anon | denied | ready
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);

  const passwordLogin = async (e) => {
    e.preventDefault();
    if (!creds.email.trim() || !creds.password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoggingIn(true);
    try {
      const res = await api.post("/auth/login", {
        email: creds.email.trim(),
        password: creds.password,
      });
      if (res.data?.token) setToken(res.data.token);
      setUser(res.data);
      setStatus("ready");
      setCreds({ email: "", password: "" });
      toast.success("Signed in.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const res = await api.get("/contact");
      setLeads(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setStatus("denied");
      else if (e?.response?.status === 401) setStatus("anon");
      else toast.error("Failed to load leads.");
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    const check = async () => {
      if (user) {
        if (!user.is_admin) return setStatus("denied");
        setStatus("ready");
        return;
      }
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setStatus(res.data.is_admin ? "ready" : "denied");
      } catch {
        setStatus("anon");
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === "ready") loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors on logout */
    }
    clearToken();
    setUser(null);
    setStatus("anon");
    toast.success("Signed out.");
  };

  const exportCsv = () => {
    if (!leads.length) {
      toast.error("No leads to export.");
      return;
    }
    const headers = ["Name", "Email", "Phone", "Interest", "Message", "Received"];
    const esc = (v) => {
      let s = String(v ?? "");
      if (/^[\s]*[=+\-@]/.test(s)) s = "'" + s; // neutralize spreadsheet formula injection
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = leads.map((l) =>
      [l.name, l.email, l.phone || "", l.interest || "", l.message, l.created_at || ""]
        .map(esc)
        .join(",")
    );
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `goodliving-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${leads.length} lead${leads.length > 1 ? "s" : ""}.`);
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="animate-spin text-gl-gold" size={40} />
      </div>
    );
  }

  if (status === "anon") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-28">
        <div className="max-w-md w-full border border-gl-gold/20 bg-white/[0.03] backdrop-blur-xl p-10 md:p-12">
          <div className="text-center">
            <div className="font-serif-display text-3xl text-gl-text mb-2">Admin Portal</div>
            <p className="text-gl-text2 mb-8">Sign in to view submitted consultation leads.</p>
          </div>

          <form onSubmit={passwordLogin} className="space-y-4 text-left" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="block text-xs uppercase tracking-[0.18em] text-gl-text2 mb-2">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                value={creds.email}
                onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                placeholder="you@email.com"
                className="w-full bg-transparent border border-gl-gold/25 px-4 py-3 text-gl-text placeholder:text-gl-text2/40 outline-none focus:border-gl-gold transition-colors"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs uppercase tracking-[0.18em] text-gl-text2 mb-2">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={creds.password}
                onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                placeholder="••••••••"
                className="w-full bg-transparent border border-gl-gold/25 px-4 py-3 text-gl-text placeholder:text-gl-text2/40 outline-none focus:border-gl-gold transition-colors"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-password-login"
              disabled={loggingIn}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gl-gold text-gl-bg font-medium hover:bg-gl-goldHover transition-colors disabled:opacity-60"
            >
              {loggingIn ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
              Sign in with Email
            </button>
          </form>

          <div className="flex items-center gap-4 my-7">
            <span className="h-px flex-1 bg-gl-gold/20" />
            <span className="text-gl-text2/60 text-xs uppercase tracking-[0.2em]">or</span>
            <span className="h-px flex-1 bg-gl-gold/20" />
          </div>

          <button
            data-testid="admin-google-login"
            onClick={startLogin}
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 border border-gl-gold/40 text-gl-text hover:border-gl-gold hover:text-gl-gold transition-colors"
          >
            <LogIn size={18} /> Continue with Google
          </button>
        </div>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center border border-gl-gold/20 bg-white/[0.03] backdrop-blur-xl p-12">
          <ShieldAlert className="mx-auto text-gl-gold mb-4" size={40} />
          <div className="font-serif-display text-2xl text-gl-text mb-2">Access Restricted</div>
          <p className="text-gl-text2 mb-6">
            {user?.email} is not authorised to view leads. Ask the site owner to add your email to the admin list.
          </p>
          <button
            data-testid="admin-logout-denied"
            onClick={logout}
            className="inline-flex items-center gap-2 px-6 py-3 border border-gl-gold/40 text-gl-text hover:text-gl-gold transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-gl-gold uppercase tracking-[0.3em] text-xs mb-3 block">
              Admin Dashboard
            </span>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light text-gl-text">
              Consultation Leads
            </h1>
            <p className="text-gl-text2 mt-2">
              Signed in as {user?.name || user?.email}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh"
              onClick={loadLeads}
              className="inline-flex items-center gap-2 px-5 py-3 border border-gl-gold/40 text-gl-text hover:text-gl-gold transition-colors"
            >
              <RefreshCw size={16} className={loadingLeads ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-5 py-3 border border-gl-gold/40 text-gl-text hover:text-gl-gold transition-colors"
            >
              <Download size={16} /> Export CSV
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-5 py-3 bg-gl-gold text-gl-bg hover:bg-gl-goldHover transition-colors"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="border border-gl-gold/20 bg-gl-bg2/40 p-6">
            <div className="font-serif-display text-4xl text-gl-gold">{leads.length}</div>
            <div className="text-sm text-gl-text2 mt-1">Total leads</div>
          </div>
        </div>

        {loadingLeads ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-gl-gold" size={32} />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20 border border-gl-gold/15 bg-white/[0.02]">
            <Inbox className="mx-auto text-gl-text2 mb-3" size={36} />
            <p className="text-gl-text2">No leads yet. New submissions will appear here.</p>
          </div>
        ) : (
          <div className="border border-gl-gold/15 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="leads-table">
                <thead>
                  <tr className="bg-gl-bg2/60 text-gl-gold text-xs uppercase tracking-[0.15em]">
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Contact</th>
                    <th className="px-5 py-4 font-medium">Interest</th>
                    <th className="px-5 py-4 font-medium">Message</th>
                    <th className="px-5 py-4 font-medium">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr
                      key={l.id || i}
                      data-testid={`lead-row-${i}`}
                      className="border-t border-gl-gold/10 hover:bg-white/[0.03] transition-colors align-top"
                    >
                      <td className="px-5 py-4 text-gl-text whitespace-nowrap">{l.name}</td>
                      <td className="px-5 py-4 text-gl-text2 text-sm">
                        <div className="flex items-center gap-2"><Mail size={13} className="text-gl-gold/70" />{l.email}</div>
                        {l.phone && <div className="flex items-center gap-2 mt-1"><Phone size={13} className="text-gl-gold/70" />{l.phone}</div>}
                      </td>
                      <td className="px-5 py-4 text-gl-text2 text-sm whitespace-nowrap">{l.interest || "—"}</td>
                      <td className="px-5 py-4 text-gl-text2 text-sm max-w-xs">{l.message}</td>
                      <td className="px-5 py-4 text-gl-text2 text-sm whitespace-nowrap">
                        {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
